import { z } from 'zod';
import type { TransactBrowserJobStatus } from '@yrp/shared/transact-browser-fill';
import {
  FlagSearchParam,
  PositiveIntegerSearchParam,
  enumSearchParam,
  positiveInt4,
  searchValidator,
} from '../../lib/search-params.js';

/**
 * Search params for the Transactions routes. Imported by router.tsx, so this
 * module stays in the entry chunk: zod and the search-param helpers only, and
 * `@yrp/shared` as types. transactions-search.test.ts checks the hand-written
 * lists against the shared values at run time.
 *
 * Never in these URLs: the table search text (history state `txQ`), the
 * TransactionDesk account (On-Behalf-Of) context or applied TransactionDesk
 * filters, and anything that would reopen a create/edit/delete/sign dialog.
 * `agent` is a Lone Wolf user id used as a display filter only — never an
 * acting-as value (the acting user is the session's).
 */

// ---- /transactions ---------------------------------------------------------

/** Source sub-tabs other than the default (Transact, the absent key). */
export const TRANSACTION_LIST_SOURCES = ['td', 'all'] as const;
export type TransactionListSource = (typeof TRANSACTION_LIST_SOURCES)[number];

/** Lone Wolf deal types the Type filter offers (TRANSACT_TRANSACTION_TYPE_LABEL keys). */
export const TRANSACT_LIST_TYPES = [0, 1, 3, 4] as const;
export type TransactListType = (typeof TRANSACT_LIST_TYPES)[number];

export const TRANSACT_LIST_SORT_KEYS = [
  'client',
  'agent',
  'type',
  'property',
  'status',
  'price',
  'listDate',
  'modified',
  'yrp',
] as const;
/** A column, or `none` for Lone Wolf's own order (the third click). Absent = the default modified/desc, not chosen. */
export const TRANSACT_LIST_SORTS = [...TRANSACT_LIST_SORT_KEYS, 'none'] as const;
export type TransactListSort = (typeof TRANSACT_LIST_SORTS)[number];

export const TRANSACT_LIST_SORT_DIRS = ['asc', 'desc'] as const;
export const TRANSACT_LIST_YRP_FILTERS = ['adopted', 'not'] as const;
export const TRANSACTION_DESK_LINK_FILTERS = ['linked', 'unlinked'] as const;

/**
 * The Status filter's namespaced values (`status:<code>`, `review:any|drift|unset`,
 * `lw:<phase code>`; see TransactStatusFilterValue in @yrp/shared). Checked by
 * shape only: the page already lets a value that matches no option lapse.
 */
const TRANSACT_STATUS_FILTER_PATTERN = /^(?:status:[a-z][a-z_]{0,39}|review:(?:any|drift|unset)|lw:\d{1,6})$/;

/** A Lone Wolf user id (a GUID, lower-cased by the page): hex digits and hyphens only. */
const TRANSACT_AGENT_PATTERN = /^[0-9a-f](?:[0-9a-f-]{0,62}[0-9a-f])?$/;

const TransactListTypeParam = z.preprocess((value) => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d{1,2}$/.test(value) ? Number(value) : Number.NaN;
  return (TRANSACT_LIST_TYPES as readonly number[]).includes(n) ? n : undefined;
}, z.union([z.literal(0), z.literal(1), z.literal(3), z.literal(4)]).optional());

const TransactStatusFilterParam = z.preprocess(
  (value) => (typeof value === 'string' && TRANSACT_STATUS_FILTER_PATTERN.test(value) ? value : undefined),
  z.string().optional(),
);

const TransactAgentParam = z.preprocess((value) => {
  if (typeof value !== 'string') return undefined;
  const id = value.toLowerCase();
  return TRANSACT_AGENT_PATTERN.test(id) ? id : undefined;
}, z.string().optional());

export const TransactionListSearchSchema = z.object({
  source: enumSearchParam(z.enum(TRANSACTION_LIST_SOURCES)),
  type: TransactListTypeParam,
  status: TransactStatusFilterParam,
  agent: TransactAgentParam,
  yrp: enumSearchParam(z.enum(TRANSACT_LIST_YRP_FILTERS)),
  sort: enumSearchParam(z.enum(TRANSACT_LIST_SORTS)),
  dir: enumSearchParam(z.enum(TRANSACT_LIST_SORT_DIRS)),
  link: enumSearchParam(z.enum(TRANSACTION_DESK_LINK_FILTERS)),
});

/** `validateSearch` for `/transactions`. Never throws; unknown keys are stripped. */
export const transactionListSearch = searchValidator(TransactionListSearchSchema);
export type TransactionListSearch = ReturnType<typeof transactionListSearch>;

// ---- /transactions/transact/$transactionId ---------------------------------

/** File page tabs other than the default (Paperwork, the absent key). */
export const TRANSACT_FILE_TABS = ['details', 'parties', 'signings', 'checklist', 'timeline', 'history'] as const;
export type TransactFileTab = (typeof TRANSACT_FILE_TABS)[number];

/** The open paperwork item, always with its kind prefix — a form and an upload have different id spaces. */
export type TransactFileItem = `form:${number}` | `doc:${string}`;

const PAPERWORK_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `form:<attached form id>` (int4) or `doc:<paperwork GUID>`; a bare id or anything else is dropped. */
export function transactFileItem(value: unknown): TransactFileItem | undefined {
  if (typeof value !== 'string') return undefined;
  if (value.startsWith('form:')) {
    const id = positiveInt4(value.slice(5));
    return id === undefined ? undefined : `form:${id}`;
  }
  if (value.startsWith('doc:')) {
    const id = value.slice(4);
    return PAPERWORK_ID_PATTERN.test(id) ? `doc:${id}` : undefined;
  }
  return undefined;
}

const TransactFileItemParam = z.preprocess(
  transactFileItem,
  z.custom<TransactFileItem>((value) => typeof value === 'string').optional(),
);

export const TransactFileSearchSchema = z.object({
  tab: enumSearchParam(z.enum(TRANSACT_FILE_TABS)),
  item: TransactFileItemParam,
});

/** `validateSearch` for `/transactions/transact/$transactionId`. Never throws; unknown keys are stripped. */
export const transactFileSearch = searchValidator(TransactFileSearchSchema);
export type TransactFileSearch = ReturnType<typeof transactFileSearch>;

// ---- /admin/checklists and /transactions/checklist-templates ----------------

export const ChecklistTemplatesSearchSchema = z.object({
  /** The template open in the editor. */
  template: PositiveIntegerSearchParam,
  /** Show deleted templates. */
  deleted: FlagSearchParam,
});

/** `validateSearch` for both checklist-template routes (same component). Never throws. */
export const checklistTemplatesSearch = searchValidator(ChecklistTemplatesSearchSchema);
export type ChecklistTemplatesSearch = ReturnType<typeof checklistTemplatesSearch>;

// ---- /transactions/browser-jobs --------------------------------------------

/** Job statuses the list can filter on (absent = all jobs). */
export const BROWSER_JOB_STATUS_FILTERS = [
  'queued',
  'running',
  'needs_login',
  'needs_attention',
  'awaiting_approval',
  'ready_for_review',
  'failed',
  'cancelled',
  'approved',
] as const satisfies ReadonlyArray<TransactBrowserJobStatus>;
const _browserJobStatusesComplete: [Exclude<TransactBrowserJobStatus, (typeof BROWSER_JOB_STATUS_FILTERS)[number]>] extends [never]
  ? true
  : never = true;
void _browserJobStatusesComplete;

export const BrowserJobsSearchSchema = z.object({
  status: enumSearchParam(z.enum(BROWSER_JOB_STATUS_FILTERS)),
});

/** `validateSearch` for `/transactions/browser-jobs`. Never throws. */
export const browserJobsSearch = searchValidator(BrowserJobsSearchSchema);
export type BrowserJobsSearch = ReturnType<typeof browserJobsSearch>;
